/* istanbul ignore file */
import { type Context, CONTEXT_CODES } from '@/types/permissions/context';

export const RTT_CATEGORIES = {
  GOOD: 'GOOD',
  MEDIUM: 'MEDIUM',
  BLOCKED: 'BLOCKED'
} as const;

export type RttCategory = (typeof RTT_CATEGORIES)[keyof typeof RTT_CATEGORIES];

// Category → chip colour. Single source of truth shared by BMTrackingChip (table
// cells) and RTTCategoryTooltip (legend key) so their colours never drift apart.
export const RTT_CATEGORY_COLORS: Record<RttCategory, string> = {
  [RTT_CATEGORIES.GOOD]: 'primary',
  [RTT_CATEGORIES.MEDIUM]: 'warning',
  [RTT_CATEGORIES.BLOCKED]: 'error'
};

// Falls back to 'secondary' for non-RTT values (e.g. cloud-tracking chips).
export const getRttCategoryColor = (category: string): string =>
  RTT_CATEGORY_COLORS[category as RttCategory] ?? 'secondary';

export const SORT_ORDER = {
  ASCENDING: 'asc',
  DESCENDING: 'desc'
} as const;

export type SortOrder = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];

export const contexts: Context[] = [
  {
    index: 0,
    code: CONTEXT_CODES.ADMIN,
    icon: 'huge:server-stack',
    svg: '$server-stack',
    name: 'admin',
    path: 'administration'
  },
  {
    index: 1,
    code: CONTEXT_CODES.DEPOT,
    icon: 'huge:shipping-loading',
    svg: '$shipping-loading',
    name: 'depots',
    path: 'depot'
  },
  {
    index: 2,
    code: CONTEXT_CODES.TENANT,
    icon: 'huge:office',
    svg: '$office',
    name: 'tenants',
    path: 'tenant'
  },
  {
    index: 3,
    code: CONTEXT_CODES.SERVICE,
    icon: 'huge:office',
    svg: '$office',
    name: 'services',
    path: 'services'
  },
  {
    index: 4,
    code: CONTEXT_CODES.SUBCONTRACTOR,
    icon: 'huge:user-group',
    // Landing renders `context.svg` via <v-icon>; `$`-aliases resolve against the
    // custom SVG set (no user-group there), so use the huge set directly.
    svg: 'huge:user-group',
    name: 'subcontractors',
    path: 'subcontractor'
  }
];

const constants = {
  contextNames: {
    admin: 'admin',
    depots: 'depots',
    services: 'services',
    tenants: 'tenants',
    subcontractors: 'subcontractors'
  },
  rolesContexts: {
    ADMIN: 'ADMIN',
    TENANT: 'TENANT',
    DEPOT: 'DEPOT',
    SUBCONTRACTOR: 'SUBCONTRACTOR'
  },
  contexts: contexts,
  errors: {
    BAD_REQUEST: 'badRequest',
    CHANGE_SCOPE: 'changeScope',
    FILE_NOT_FOUND: 'fileNotFound',
    NO_DATA_PERMISSION: 'noDataPermission',
    NO_DC_PERMISSION: 'noPermission',
    NO_MATCHING_DC: 'noMatchingPermission',
    NO_PERMISSION: 'noPermission',
    NOT_FOUND: '404'
  },
  jobTypes: {
    COLLECTION: 'COLLECTION',
    DELIVERY: 'DELIVERY',
    PICKUP: 'PICKUP',
    CUSTOM: 'CUSTOM',
    UNKNOWN: 'UNKNOWN'
  },
  permissionScopes: {
    ADMIN: 'ADMIN',
    APP: 'APP',
    COUNTRY: 'COUNTRY'
  },
  roles: {
    ADMIN: 'Admin',
    DRIVER: 'DRIVER',
    TEMP_DRIVER: 'TEMP_DRIVER'
  },
  scopeTypes: {
    ADMIN: 'Admin',
    DISTRIBUTION_CENTER: 'DistributionCenter',
    TENANT: 'Tenant',
    PROFILE: 'Profile',
    SUBCONTRACTOR: 'Subcontractor'
  },
  waypointTypes: {
    DONE: 'DONE',
    OPEN: 'OPEN',
    UNASSIGNED: 'UNASSIGNED',
    CURRENT: 'CURRENT',
    extraMarkers: { DRIVER: 'DRIVER', DEPOT: 'DEPOT' }
  },
  sequenceOptimisationTypes: {
    FREE: 'FREE_SEQUENCE_OPTIMISATION',
    FIXED: 'FIXED_SEQUENCE_OPTIMISATION',
    PREVIOUS: 'PREVIOUS_SEQUENCE_OPTIMISATION'
  },
  waypointStatuses: {
    CONFIRMED: 'CONFIRMED',
    PROCESSED: 'PROCESSED',
    UNPROCESSED: 'UNPROCESSED'
  },
  jobAssignmentStatuses: {
    PROCESSED: 'PROCESSED',
    SUCCESSFUL: 'SUCCESSFUL',
    UNPROCESSED: 'UNPROCESSED'
  },
  addressCorrectionState: {
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    PENDING: 'PENDING'
  },
  table: {
    DEFAULT_NR_OF_ROWS: 25,
    MAX_NR_OF_FILTERS_PER_ROW: 4,
    ASCENDING: SORT_ORDER.ASCENDING,
    DESCENDING: SORT_ORDER.DESCENDING
  },
  drivenHistory: {
    NR_OF_DAYS_DISPLAY_TDH: 30
  },
  mapIds: {
    TDH_DRIVEN_MAP: '40309f5b67a9db15',
    LAST_METER_MAP: '947a0faa0f0b597c',
    DEPOT_LANDING_PAGE_MAP: '6cc12a9a566538bbe1eae700',
    TOUR_COMMANDER_MAP: '6cc12a9a566538bbb7e5f068',
    DEPOT_CORE_DATA_MAP: '6cc12a9a566538bbe778f2bd',
    DEPOT_TOUR_DETAILS_MAP: '6cc12a9a566538bb800cf8e8',
    ADDRESS_LOCATION_CORRECTION_MAP: '6cc12a9a566538bbd53e8075'
  },
  tourTypes: {
    NTC: 'NTC',
    TCG: 'TCG',
    USER: 'USER'
  },
  rttCategories: RTT_CATEGORIES,
  internalEmails: ['bettermile.com', 'gls-ecl.com'],
  coordinateTypes: {
    ROOFTOP: 'ROOFTOP',
    STREET: 'STREET',
    HANDOVER: 'HANDOVER'
  },
  developerPortalLink: 'https://developers.bettermile.com/get-started'
};

export default constants;
